import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  Users, UserCheck, UserX, Calendar, ClipboardList,
  Mail, Image, ChevronRight, TrendingUp, Star,
  Trash2, Shield, Download, Upload, RefreshCw, ShieldAlert,
  Award, Send, History, KeyRound
} from 'lucide-react';

const API_URL = '';

// Map action_type → { icon, color, label }
const ACTION_META = {
  delete_member:        { Icon: Trash2,      color: 'text-red-600',     bg: 'bg-red-50',     label: 'Deleted member' },
  delete_subscriber:    { Icon: Trash2,      color: 'text-red-600',     bg: 'bg-red-50',     label: 'Deleted subscriber' },
  delete_exhibition:    { Icon: Trash2,      color: 'text-red-600',     bg: 'bg-red-50',     label: 'Deleted exhibition' },
  delete_call:          { Icon: Trash2,      color: 'text-red-600',     bg: 'bg-red-50',     label: 'Deleted call for submissions' },
  delete_category:      { Icon: Trash2,      color: 'text-red-600',     bg: 'bg-red-50',     label: 'Deleted category' },
  revoke_judge_token:   { Icon: KeyRound,    color: 'text-amber-700',   bg: 'bg-amber-50',   label: 'Revoked judge link' },
  grant_admin:          { Icon: Shield,      color: 'text-amber-700',   bg: 'bg-amber-50',   label: 'Granted admin access' },
  import_from_cc:       { Icon: Download,    color: 'text-blue-700',    bg: 'bg-blue-50',    label: 'Imported from Constant Contact' },
  sync_to_cc:           { Icon: Upload,      color: 'text-blue-700',    bg: 'bg-blue-50',    label: 'Synced to Constant Contact' },
  reset_sync_errors:    { Icon: RefreshCw,   color: 'text-amber-700',   bg: 'bg-amber-50',   label: 'Reset sync errors' },
  mark_spam:            { Icon: ShieldAlert, color: 'text-amber-700',   bg: 'bg-amber-50',   label: 'Marked as spam' },
  publish_winners:      { Icon: Award,       color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Published winners' },
  bulk_email_sent:      { Icon: Send,        color: 'text-purple-700',  bg: 'bg-purple-50',  label: 'Sent bulk email' },
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentRegs, setRecentRegs] = useState([]);
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [upcomingWorkshops, setUpcomingWorkshops] = useState([]);
  const [actionLog, setActionLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) fetchAll();
  }, [token]);

  const fetchAll = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const today = new Date().toISOString().split('T')[0];

      const [memberStats, workshopsRes, regsRes, inquiriesRes, actionLogRes] = await Promise.all([
        fetch(`${API_URL}/api/members/directory/stats`, { headers }),
        fetch(`${API_URL}/api/content/workshops`),
        fetch(`${API_URL}/api/registrations`, { headers }),
        fetch(`${API_URL}/api/contact/submissions?limit=5`, { headers }),
        fetch(`${API_URL}/api/admin/action-log?limit=10`, { headers }),
      ]);

      if (memberStats.ok) setStats(await memberStats.json());

      if (workshopsRes.ok) {
        const ws = await workshopsRes.json();
        const upcoming = ws
          .filter(w => w.date && w.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date))
          .slice(0, 5);
        setUpcomingWorkshops(upcoming);
      }

      if (regsRes.ok) {
        const regs = await regsRes.json();
        setRecentRegs(
          (Array.isArray(regs) ? regs : [])
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
        );
      }

      if (inquiriesRes.ok) {
        const inq = await inquiriesRes.json();
        setRecentInquiries(Array.isArray(inq) ? inq.slice(0, 5) : []);
      }

      if (actionLogRes.ok) {
        const log = await actionLogRes.json();
        setActionLog(Array.isArray(log) ? log : []);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return s; }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard" subtitle="Academy overview">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard" subtitle="Academy at a glance">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Member Stats */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-stone-700 uppercase tracking-wide">Members</h2>
            <Link to="/admin/members" className="text-xs text-cafa-red hover:underline flex items-center gap-0.5">
              Manage <ChevronRight size={13} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Members', value: stats?.total ?? '—', icon: Users, color: 'text-stone-700', bg: 'bg-stone-100' },
              { label: 'Active', value: stats?.active ?? '—', icon: UserCheck, color: 'text-green-700', bg: 'bg-green-100' },
              { label: 'Lifetime', value: stats?.lifetime ?? '—', icon: Star, color: 'text-amber-700', bg: 'bg-amber-100' },
              { label: 'Inactive', value: stats?.inactive ?? '—', icon: UserX, color: 'text-stone-400', bg: 'bg-stone-100' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <Card key={label} className="border-stone-200">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} className={color} />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">{label}</p>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Upcoming Workshops */}
          <section className="lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-stone-700 uppercase tracking-wide">Upcoming Workshops</h2>
              <Link to="/admin/workshops" className="text-xs text-cafa-red hover:underline flex items-center gap-0.5">
                All <ChevronRight size={13} />
              </Link>
            </div>
            <Card className="border-stone-200">
              <CardContent className="p-0 divide-y divide-stone-100">
                {upcomingWorkshops.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-stone-400">No upcoming workshops scheduled.</p>
                ) : upcomingWorkshops.map(w => (
                  <div key={w.id} className="px-4 py-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{w.title}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{w.instructor}</p>
                    </div>
                    <span className="text-xs text-stone-400 whitespace-nowrap flex-shrink-0 mt-0.5 flex items-center gap-1">
                      <Calendar size={11} /> {w.date}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>

          {/* Recent Registrations */}
          <section className="lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-stone-700 uppercase tracking-wide">Recent Registrations</h2>
              <Link to="/admin/registrations" className="text-xs text-cafa-red hover:underline flex items-center gap-0.5">
                All <ChevronRight size={13} />
              </Link>
            </div>
            <Card className="border-stone-200">
              <CardContent className="p-0 divide-y divide-stone-100">
                {recentRegs.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-stone-400">No registrations yet.</p>
                ) : recentRegs.map(r => (
                  <div key={r.id} className="px-4 py-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{r.payer_name}</p>
                      <p className="text-xs text-stone-500 truncate mt-0.5">
                        {r.items?.[0]?.name || r.registration_data?.program_name || '—'}
                      </p>
                    </div>
                    <span className="text-xs text-stone-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                      {fmtDate(r.created_at)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>

          {/* Recent Contact Inquiries */}
          <section className="lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-stone-700 uppercase tracking-wide">Recent Inquiries</h2>
              <Link to="/admin/contact-analytics" className="text-xs text-cafa-red hover:underline flex items-center gap-0.5">
                All <ChevronRight size={13} />
              </Link>
            </div>
            <Card className="border-stone-200">
              <CardContent className="p-0 divide-y divide-stone-100">
                {recentInquiries.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-stone-400">No recent inquiries.</p>
                ) : recentInquiries.map(inq => (
                  <div key={inq.id} className="px-4 py-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{inq.name}</p>
                      <p className="text-xs text-stone-500 truncate mt-0.5">{inq.subject}</p>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
                      {inq.status === 'new' && (
                        <span className="text-xs px-1.5 py-0.5 bg-cafa-red text-white rounded-full font-medium">New</span>
                      )}
                      <span className="text-xs text-stone-400">{fmtDate(inq.created_at)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Recent Admin Actions (read-only audit trail) */}
        <section data-testid="admin-action-log-widget">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-stone-700 uppercase tracking-wide flex items-center gap-2">
              <History size={16} className="text-stone-500" />
              Recent Admin Actions
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-stone-200 text-stone-600 rounded uppercase tracking-wide">
                Read-only
              </span>
            </h2>
            <span className="text-xs text-stone-400">Last {actionLog.length} {actionLog.length === 1 ? 'action' : 'actions'}</span>
          </div>
          <Card className="border-stone-200">
            <CardContent className="p-0 divide-y divide-stone-100">
              {actionLog.length === 0 ? (
                <div className="px-4 py-8 text-center" data-testid="admin-action-log-empty">
                  <History size={28} className="mx-auto mb-2 text-stone-300" />
                  <p className="text-sm text-stone-400">No high-risk actions logged yet.</p>
                  <p className="text-xs text-stone-400 mt-1">
                    Deletes, imports, syncs, admin grants, spam marks, judge link revocations, winner publishes, and bulk emails are recorded here once performed.
                  </p>
                </div>
              ) : actionLog.map(entry => {
                const meta = ACTION_META[entry.action_type] || {
                  Icon: History, color: 'text-stone-700', bg: 'bg-stone-100', label: entry.action_type
                };
                const { Icon, color, bg, label } = meta;
                return (
                  <div
                    key={entry.id}
                    className="px-4 py-3 flex items-start gap-3"
                    data-testid={`action-log-row-${entry.action_type}`}
                  >
                    <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon size={14} className={color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${color}`}>{label}</span>
                        {entry.target_label && (
                          <span className="text-sm text-stone-700 truncate">— {entry.target_label}</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">
                        by <span className="font-medium text-stone-600">{entry.admin_name || entry.admin_email || 'admin'}</span>
                        {entry.metadata?.count !== undefined && entry.action_type === 'bulk_email_sent' && (
                          <> · {entry.metadata.count} recipient{entry.metadata.count === 1 ? '' : 's'}</>
                        )}
                        {entry.metadata?.count !== undefined && entry.action_type === 'reset_sync_errors' && (
                          <> · {entry.metadata.count} subscriber{entry.metadata.count === 1 ? '' : 's'}</>
                        )}
                        {entry.action_type === 'import_from_cc' && entry.metadata?.imported !== undefined && (
                          <> · {entry.metadata.imported} new, {entry.metadata.skipped || 0} skipped</>
                        )}
                      </p>
                    </div>
                    <span
                      className="text-xs text-stone-400 whitespace-nowrap flex-shrink-0 mt-1"
                      title={entry.created_at}
                    >
                      {fmtRelative(entry.created_at)}
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>

        {/* Quick Links */}
        <section>
          <h2 className="text-base font-semibold text-stone-700 uppercase tracking-wide mb-3">Quick Links</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { to: '/admin/members', label: 'Members', icon: Users },
              { to: '/admin/workshops', label: 'Workshops', icon: Calendar },
              { to: '/admin/content', label: 'Programs', icon: ClipboardList },
              { to: '/admin/registrations', label: 'Registrations', icon: TrendingUp },
              { to: '/admin/contact-analytics', label: 'Contact Analytics', icon: Mail },
              { to: '/admin/exhibitions', label: 'Exhibitions', icon: Image },
            ].map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-700 hover:border-cafa-red hover:text-cafa-red transition-colors"
              >
                <Icon size={14} /> {label}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
